import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Briefcase, MapPin, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ResourceButtons } from '../components/ResourceButtons';
import { CTASection } from '../components/CTASection';
import api from '../utils/api';
import { getUser } from '../utils/auth';

export default function Home() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = getUser();

  useEffect(() => {
    fetchAllJobs();
  }, []);

  const fetchAllJobs = async () => {
    try {
      setLoading(true);
      // Fetch both our jobs and Zoho jobs
      const [ourJobsRes, zohoJobsRes] = await Promise.all([
        api.get('/jobs?status=active'),
        api.get('/zoho/jobs').catch(() => ({ data: [] }))
      ]);
      
      // Mark jobs with their source
      const ourJobs = ourJobsRes.data.map(job => ({ ...job, source: 'internal' }));
      const zohoJobs = zohoJobsRes.data.map(job => ({ ...job, source: 'zoho' }));
      
      // Combine with Zoho jobs FIRST, then internal jobs, limit to 12 for featured
      const combined = [...zohoJobs, ...ourJobs].slice(0, 12);
      setAllJobs(combined);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);
    if (locationQuery) params.append('location', locationQuery);
    if (isEmbed) params.append('embed', 'true');
    navigate(`/jobs?${params.toString()}`);
  };

  const formatSalary = (job) => {
    const min = job.salary_min || 0;
    const max = job.salary_max || 0;
    
    if (min === 0 && max === 0) {
      return 'Negotiable';
    }
    
    const salary = max || min;
    
    if (salary < 1000) {
      const formatHourly = (val) => {
        const formatted = val.toFixed(2);
        return formatted.endsWith('.00') ? val.toString() : formatted;
      };
      
      if (min > 0 && max > 0 && min !== max) {
        return `$${formatHourly(min)}-$${formatHourly(max)}/hr`;
      }
      return `$${formatHourly(salary)}/hr`;
    }
    
    if (min > 0 && max > 0 && min !== max) {
      return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k/yr`;
    }
    return `$${(salary / 1000).toFixed(0)}k/yr`;
  };

  const handleJobClick = (job) => {
    const embedParam = isEmbed ? '?embed=true' : '';
    if (job.source === 'zoho') {
      navigate(`/jobs/zoho/${job.id}${embedParam}`);
    } else {
      navigate(`/jobs/${job.id}${embedParam}`);
    }
  };

  const truncateDescription = (desc, maxLength = 120) => {
    if (!desc) return 'No description available';
    const stripped = desc.replace(/<[^>]*>/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Helmet>
        <title>Can X Global | Find Your Dream Job in Canada</title>
        <meta name="description" content="Discover thousands of job opportunities across Canada. Connect with top employers, apply online, and start your career journey with Can X Global." />
        <meta property="og:title" content="Can X Global | Find Your Dream Job in Canada" />
        <meta property="og:description" content="Discover thousands of job opportunities across Canada. Connect with top employers and start your career journey today." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://canxglobal.com/" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="hero-gradient py-12 md:py-24 px-4 md:px-6 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute top-20 right-10 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl hidden md:block"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl hidden md:block"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-8 md:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-extrabold mb-4 md:mb-8 leading-tight" data-testid="hero-heading">
              <span className="text-black">Your Next Career Move </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gradient-start to-brand-gradient-end">Starts Here!</span>
            </h1>
            
            <p className="text-base md:text-xl lg:text-2xl text-zinc-600 max-w-3xl mx-auto mb-6 md:mb-12 font-medium px-2" data-testid="hero-subheading">
              Search. Apply. Succeed. Thousands of opportunities, all at your fingertips.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-5xl mx-auto mb-8 md:mb-16" data-testid="hero-search-form">
              <div className="glass-morphism rounded-2xl md:rounded-3xl p-3 md:p-4 shadow-xl md:shadow-2xl">
                <div className="flex flex-col gap-3 md:gap-4">
                  <div className="flex flex-col md:flex-row gap-3 md:gap-4">
                    <div className="flex-1 relative group">
                      <Search className="absolute left-4 md:left-5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400 group-focus-within:text-brand-blue transition-colors" />
                      <Input
                        type="text"
                        placeholder="Job title or keywords"
                        className="h-12 md:h-16 pl-12 md:pl-14 pr-4 md:pr-6 border-0 bg-white/50 hover:bg-white/70 focus:bg-white rounded-xl md:rounded-2xl text-sm md:text-base font-medium transition-all"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="hero-search-input"
                      />
                    </div>
                    <div className="flex-1 relative group">
                      <MapPin className="absolute left-4 md:left-5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400 group-focus-within:text-brand-blue transition-colors" />
                      <Input
                        type="text"
                        placeholder="Location"
                        className="h-12 md:h-16 pl-12 md:pl-14 pr-4 md:pr-6 border-0 bg-white/50 hover:bg-white/70 focus:bg-white rounded-xl md:rounded-2xl text-sm md:text-base font-medium transition-all"
                        value={locationQuery}
                        onChange={(e) => setLocationQuery(e.target.value)}
                        data-testid="hero-location-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="premium-button h-12 md:h-16 px-8 md:px-12 bg-gradient-to-r from-brand-gradient-start to-brand-gradient-end text-white font-bold rounded-xl md:rounded-2xl hover:shadow-2xl hover:scale-105 transition-all text-sm md:text-base w-full md:w-auto"
                    data-testid="hero-search-btn"
                  >
                    Search Jobs
                  </Button>
                </div>
              </div>
            </form>

            {/* Stats */}
            <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center gap-4 md:gap-8">
              <div className="text-center group">
                <div className="stat-card w-20 h-20 md:w-32 md:h-32 rounded-2xl md:rounded-3xl flex items-center justify-center mx-auto mb-2 md:mb-4 transition-all group-hover:scale-110 group-hover:shadow-2xl">
                  <span className="text-2xl md:text-4xl font-bold bg-gradient-to-br from-zinc-800 to-zinc-600 bg-clip-text text-transparent">97%</span>
                </div>
                <p className="text-xs md:text-sm font-semibold text-zinc-700 uppercase tracking-wider">Retention</p>
              </div>
              <div className="text-center group">
                <div className="stat-card w-20 h-20 md:w-32 md:h-32 rounded-2xl md:rounded-3xl flex items-center justify-center mx-auto mb-2 md:mb-4 transition-all group-hover:scale-110 group-hover:shadow-2xl">
                  <span className="text-2xl md:text-4xl font-bold bg-gradient-to-br from-brand-gradient-start to-brand-gradient-end bg-clip-text text-transparent">10K+</span>
                </div>
                <p className="text-xs md:text-sm font-semibold text-zinc-700 uppercase tracking-wider">Placements</p>
              </div>
              <div className="text-center group">
                <div className="stat-card w-20 h-20 md:w-32 md:h-32 rounded-2xl md:rounded-3xl flex items-center justify-center mx-auto mb-2 md:mb-4 transition-all group-hover:scale-110 group-hover:shadow-2xl">
                  <span className="text-2xl md:text-4xl font-bold bg-gradient-to-br from-cyan-400 to-cyan-600 bg-clip-text text-transparent">50%</span>
                </div>
                <p className="text-xs md:text-sm font-semibold text-zinc-700 uppercase tracking-wider">Cost Savings</p>
              </div>
              <div className="text-center group">
                <div className="stat-card w-20 h-20 md:w-32 md:h-32 rounded-2xl md:rounded-3xl flex items-center justify-center mx-auto mb-2 md:mb-4 transition-all group-hover:scale-110 group-hover:shadow-2xl">
                  <span className="text-2xl md:text-4xl font-bold bg-gradient-to-br from-cyan-600 to-blue-700 bg-clip-text text-transparent">30+</span>
                </div>
                <p className="text-xs md:text-sm font-semibold text-zinc-700 uppercase tracking-wider">Countries</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Jobs Section - Combined */}
      <section className="py-12 md:py-16 px-4 md:px-6 bg-white" data-testid="featured-jobs-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold text-zinc-900 mb-2">
                Featured Jobs
              </h2>
              <p className="text-sm md:text-base text-zinc-600">Browse current opportunities with top organizations.</p>
            </div>
            <Button
              onClick={() => navigate(`/jobs${isEmbed ? '?embed=true' : ''}`)}
              className="mt-4 md:mt-0 px-8 md:px-10 py-4 md:py-5 bg-brand-blue text-white font-bold rounded-xl hover:bg-brand-blue/90 transition-all text-base md:text-lg shadow-lg hover:shadow-xl"
            >
              View All Jobs →
            </Button>
          </div>

          {/* Jobs List */}
          {loading ? (
            <div className="text-center py-16">
              <RefreshCw className="w-10 h-10 text-zinc-400 mx-auto mb-4 animate-spin" />
              <p className="text-zinc-500">Loading jobs...</p>
            </div>
          ) : allJobs.length === 0 ? (
            <div className="text-center py-16">
              <Briefcase className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
              <p className="text-zinc-500 mb-4">No jobs available at the moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {allJobs.map((job) => (
                <div
                  key={job.id}
                  onClick={() => handleJobClick(job)}
                  className="bg-white rounded-xl p-5 md:p-6 border border-zinc-200 hover:shadow-lg hover:border-brand-blue/30 transition-all duration-200 cursor-pointer group"
                  data-testid={`featured-job-${job.id}`}
                >
                  {/* Location */}
                  <div className="flex items-center gap-2 text-zinc-500 text-xs md:text-sm mb-2">
                    <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4" strokeWidth={2} />
                    <span className="truncate">{job.location}</span>
                  </div>

                  {/* Job Title */}
                  <h3 className="text-base md:text-lg font-bold text-zinc-900 mb-2 group-hover:text-brand-blue transition-colors">
                    {job.title}
                  </h3>

                  {/* Company */}
                  <p className="text-zinc-600 text-sm mb-2">{job.company || 'Can X Global'}</p>

                  {/* Salary */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-brand-blue font-semibold text-sm">
                      {job.salary ? job.salary : formatSalary(job)}
                    </span>
                  </div>

                  {/* Description Preview */}
                  <p className="text-zinc-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-4">
                    {truncateDescription(job.description)}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
                    <span className="px-2.5 py-1 bg-cyan-50 text-cyan-700 rounded-full text-xs font-medium capitalize">
                      {job.job_type?.replace('-', ' ') || 'Full Time'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section with Resume Form */}
      <CTASection />

      {/* Resource Section */}
      <ResourceButtons />
    </div>
  );
}
